/** To be used as default prop */
export const emptyArray = [];
/** To be used as default prop */
export const emptyObject = {};
/** To be used as default prop */
/* eslint-disable-next-line unicorn/prevent-abbreviations --
Canonical name from https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/no-object-type-as-default-prop.md */
export const noopFunc = () => {
  /* no-op */
};

/** Most stations a show can be set up with. */
export const MAX_STATIONS = 6;

/**
 * Video output types a capture station can be fed from, shared by the submission form and the
 * schedule editor's run of show so the two always offer the same choices.
 * `translationKey` is set for the ones whose label isn't a proper noun.
 */
export const videoOutputs = [
  { value: 'composite', label: 'Composite' },
  { value: 'svideo', label: 'S-Video' },
  { value: 'component', label: 'Component' },
  { value: 'hdmi', label: 'HDMI' },
  { value: 'other', translationKey: 'run.videoOutput.other' },
];

/**
 * Languages a runner can be marked as understanding, shared by the schedule editor's run of show
 * and its public display. `fr`/`en` are named through `Intl.DisplayNames` rather than here, so
 * they read in whatever language the viewer has picked.
 */
export const runnerLanguages = [
  { value: 'unknown', translationKey: 'runOfShow.runnerLanguage.unknown' },
  { value: 'fr' },
  { value: 'en' },
  { value: 'both', translationKey: 'run.commentaryLanguage.both' },
];

export const defaultSubmission = {
  id: undefined,
  name: '',
  category: '',
  estimate: '',
  platform: '',
  videoUrl: '',
  stationsCount: '',
  networkAccess: '',
  videoOutput: '',
  comment: '',
  isRace: false,
  isCoop: false,
  raceCode: '',
  status: 0,
  event: '',
  emulator: false,
};

export const defaultAttendance = [
  {
    question: 'attendance.hotel.title',
    list: [],
    links: [],
    answer: ['attendance.hotel.p1'],
  },
  {
    question: 'attendance.booking.title',
    list: [],
    links: [],
    answer: ['attendance.booking.p1', 'attendance.booking.p2'],
  },
  {
    question: 'attendance.parking.title',
    list: [],
    links: [],
    answer: ['attendance.parking.p1'],
  },
  {
    question: 'attendance.amenities.title',
    list: [],
    links: [],
    answer: ['attendance.amenities.p1', 'attendance.amenities.p2'],
  },
  {
    question: 'attendance.safety.title',
    list: [],
    links: [],
    answer: ['attendance.safety.p1', 'attendance.safety.p2'],
  },
  {
    question: 'attendance.transportation.title',
    list: [],
    links: [],
    answer: [
      'attendance.transportation.p1',
      'attendance.transportation.p2',
      'attendance.transportation.p3',
    ],
  },
  {
    question: 'attendance.food.title',
    list: [],
    links: [],
    answer: ['attendance.food.p1'],
  },
  {
    question: 'attendance.airport.title',
    list: [],
    links: [],
    answer: ['attendance.airport.p1'],
  },
  {
    question: 'attendance.general.title',
    list: [
      'attendance.general.li1',
      'attendance.general.li2',
      'attendance.general.li3',
      'attendance.general.li4',
      'attendance.general.li5',
      'attendance.general.li6',
      'attendance.general.li7',
    ],
    links: [null, 'https://noreset.tv/', null, null, null, null, null],
    answer: ['attendance.general.p1'],
  },
  {
    question: 'attendance.children.title',
    list: [],
    links: [],
    answer: ['attendance.children.p1', 'attendance.children.p2'],
  },
];

export const defaultActivities = [
  {
    title: 'activities.pc.title',
    description: 'activities.pc.description',
    images: [
      '/imgs/activities/PC-01.jpg',
      '/imgs/activities/PC-02.jpg',
      '/imgs/activities/PC-03.jpg',
      '/imgs/activities/PC-04.jpg',
    ],
  },
  {
    title: 'activities.console.title',
    description: 'activities.console.description',
    images: [
      '/imgs/activities/Console-01.jpg',
      '/imgs/activities/Console-02.jpg',
      '/imgs/activities/Console-03.jpg',
      '/imgs/activities/Console-04.jpg',
      '/imgs/activities/Console-05.jpg',
      '/imgs/activities/Console-06.jpg',
    ],
  },
  {
    title: 'activities.board.title',
    description: 'activities.board.description',
    images: [
      '/imgs/activities/Board-Games-01.jpg',
      '/imgs/activities/Board-Games-02.jpg',
      '/imgs/activities/Board-Games-03.jpg',
      '/imgs/activities/Board-Games-04.jpg',
      '/imgs/activities/Board-Games-05.jpg',
      '/imgs/activities/Board-Games-06.jpg',
      '/imgs/activities/Board-Games-07.jpg',
    ],
  },
  {
    title: 'activities.arcade.title',
    description: 'activities.arcade.description',
    images: [
      '/imgs/activities/Arcade-01.jpg',
      '/imgs/activities/Arcade-02.jpg',
      '/imgs/activities/Arcade-03.jpg',
      '/imgs/activities/Arcade-04.jpg',
      '/imgs/activities/Arcade-05.jpg',
      '/imgs/activities/Arcade-06.jpg',
      '/imgs/activities/Arcade-07.jpg',
    ],
  },
  {
    title: 'activities.stream.title',
    description: 'activities.stream.description',
    images: [
      '/imgs/activities/Stream-Room-01.jpg',
      '/imgs/activities/Stream-Room-02.jpg',
      '/imgs/activities/Stream-Room-03.jpg',
      '/imgs/activities/Stream-Room-04.jpg',
      '/imgs/activities/Stream-Room-05.png',
      '/imgs/activities/Stream-Room-06.jpg',
      '/imgs/activities/Stream-Room-07.jpg',
    ],
  },
  {
    title: 'activities.other.title',
    description: 'activities.other.description',
    images: [
      '/imgs/activities/Other-01.png',
      '/imgs/activities/Other-02.png',
      '/imgs/activities/Other-03.png',
      '/imgs/activities/Other-04.png',
      '/imgs/activities/Other-05.jpg',
      '/imgs/activities/Other-06.jpg',
      '/imgs/activities/Other-07.jpg',
      '/imgs/activities/Other-08.jpg',
      '/imgs/activities/Other-09.jpg',
    ],
  },
];

export const defaultRules = [
  {
    question: 'rules.attendance.title',
    answer: 'rules.attendance.p1',
  },
  {
    question: 'rules.harassment.title',
    answer: 'rules.harassment.p1',
  },
  {
    question: 'rules.food.title',
    answer: 'rules.food.p1',
  },
  {
    question: 'rules.areas.title',
    answer: 'rules.areas.p1',
  },
  {
    question: 'rules.broadcast.title',
    answer: 'rules.broadcast.p1',
  },
];

export const defaultSubmissions = [
  {
    question: 'submissions.guidelines.title',
    list: [],
    sub: null,
    answer: ['submissions.guidelines.p1', 'submissions.guidelines.p2'],
  },
  {
    question: 'submissions.runs.title',
    list: [],
    sub: null,
    answer: [
      'submissions.runs.p1',
      'submissions.runs.p2',
      'submissions.runs.p3',
      'submissions.runs.p4',
      'submissions.runs.p5',
      'submissions.runs.p6',
      'submissions.runs.p7',
      'submissions.runs.p8',
    ],
    links: [null, null, null, 'https://safety.twitch.tv/s/article/Prohibited-Games'],
  },
  {
    question: 'submissions.races.title',
    list: [],
    sub: null,
    answer: ['submissions.races.p1', 'run.stationsCount.helper', 'submissions.races.p2'],
  },
  {
    question: 'submissions.how.title',
    list: [
      'submissions.how.li1',
      'submissions.how.li2',
      'submissions.how.li3',
      'submissions.how.li4',
      'submissions.how.li5',
      'submissions.how.li6',
    ],
    sub: [
      null,
      null,
      null,
      [
        'submissions.how.li4.sub1',
        'submissions.how.li4.sub2',
        'submissions.how.li4.sub3',
        'submissions.how.li4.sub4',
      ],
      null,
      null,
    ],
    links: ['https://noreset.tv/'],
    answer: [],
  },
];

export const defaultVolunteering = [
  {
    question: 'volunteer.stream.title',
    answer: ['volunteer.stream.p1'],
  },
  {
    question: 'volunteer.sound.title',
    answer: ['volunteer.sound.p1'],
  },
  {
    question: 'volunteer.tech.title',
    answer: ['volunteer.tech.p1'],
  },
  {
    question: 'volunteer.coord.title',
    answer: ['volunteer.coord.p1'],
  },
  {
    question: 'volunteer.social.title',
    answer: ['volunteer.social.p1'],
  },
  {
    question: 'volunteer.admission.title',
    answer: ['volunteer.admission.p1'],
  },
  {
    question: 'volunteer.hosts.title',
    answer: ['volunteer.hosts.p1'],
  },
  {
    question: 'volunteer.freeplay.title',
    answer: ['volunteer.freeplay.p1'],
  },
];

export const defaultComplimentaryPassMain = {
  answer: ['volunteer.pass.description.p1', 'volunteer.pass.description.p2'],
};

export const defaultComplimentaryPass = [
  {
    question: 'volunteer.pass.counts.title',
    list: [
      'volunteer.pass.counts.li1',
      'volunteer.pass.counts.li2',
      'volunteer.pass.counts.li3',
      'volunteer.pass.counts.li4',
      'volunteer.pass.counts.li5',
      'volunteer.pass.counts.li6',
      'volunteer.pass.counts.li7',
      'volunteer.pass.counts.li8',
      'volunteer.pass.counts.li9',
      'volunteer.pass.counts.li10',
    ],
    answer: ['volunteer.pass.counts.p1'],
  },
  {
    question: 'volunteer.pass.notCounted.title',
    list: [
      'volunteer.pass.notCounted.li1',
      'volunteer.pass.notCounted.li2',
      'volunteer.pass.notCounted.li3',
      'volunteer.pass.notCounted.li4',
      'volunteer.pass.notCounted.li5',
    ],
    answer: [],
  },
  {
    question: 'volunteer.pass.negative.title',
    list: [
      'volunteer.pass.negative.li1',
      'volunteer.pass.negative.li2',
      'volunteer.pass.negative.li3',
    ],
    answer: [],
  },
  {
    question: 'volunteer.pass.addMore.title',
    list: ['volunteer.pass.addMore.li1', 'volunteer.pass.addMore.li2'],
    answer: [],
  },
  {
    question: 'volunteer.pass.guaranteed.title',
    list: [],
    answer: ['volunteer.pass.guaranteed.p1'],
  },
  {
    question: 'volunteer.pass.contact.title',
    list: [],
    answer: ['volunteer.pass.contact.p1'],
  },
];

export const defaultVolunteeringMain = {
  answer: [
    'volunteer.main.p1',
    'volunteer.main.p2',
    'volunteer.main.p3',
    'volunteer.main.p4',
    'volunteer.main.p5',
  ],
};
